import {faEnvelope, faComments} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {Modal as FreckleModal, Header, Body} from '@freckle/educator-materials/ts/components/modal'
import {
  getFAQLink,
  getAdminSupportEmail
} from '@freckle/educator-materials/ts/components/support-button/links'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {panel, panelContent, panelWrapper, panelText} from './modal.module.scss'

type Props = {
  showModal: boolean
  onCloseModal: () => void // all call sites pass the 'toggleModal' function here
  countryCode: string | null
}

export function Modal({showModal, onCloseModal, countryCode}: Props) {
  const {t} = useSafeTranslation()

  return (
    <FreckleModal show={showModal} onHide={onCloseModal} width="medium-small" height="medium-small">
      <Header onClickClose={onCloseModal}>{t('SUPPORT_MODAL_TITLE')}</Header>
      <Body>
        <div className={panelWrapper}>
          <Link
            className={panel}
            href={getFAQLink(countryCode)}
            target="_blank"
            reactRouter={false}
          >
            <div className={panelContent}>
              <i className="fa fa-question fa-4x" aria-hidden="true" />
              <div className={panelText}>
                <p>{t('SUPPORT_BUTTON_ADMIN_FAQ_TEXT')}</p>
              </div>
            </div>
          </Link>

          {countryCode === 'USA' ? (
            <Chat onCloseModal={onCloseModal} />
          ) : (
            <Email countryCode={countryCode} />
          )}
        </div>
      </Body>
    </FreckleModal>
  )
}

const loadForethoughtScripts = () => {
  return new Promise<void>((resolve, reject) => {
    if (!document.getElementById('forethought-widget-embed-script')) {
      const forethoughtScript = document.createElement('script')
      forethoughtScript.src = 'https://solve-widget.forethought.ai/embed.js'
      forethoughtScript.id = 'forethought-widget-embed-script'
      forethoughtScript.type = 'application/javascript'
      forethoughtScript.dataset.apiKey = '5791f610-77c3-450b-9cef-3b6c3419962d'
      forethoughtScript.dataset.ftWorkflowTag = 'Freckle_Platform'
      forethoughtScript.dataset.ftUrl = 'https://school.freckle.com'
      forethoughtScript.onload = () => resolve()
      forethoughtScript.onerror = () => reject('Failed to load Forethought script')
      document.body.appendChild(forethoughtScript)
    } else {
      resolve() // Script already loaded
    }
  })
}

const loadSalesforceScripts = () => {
  return new Promise<void>((resolve, reject) => {
    if (!document.getElementById('salesforce-bootstrap-script')) {
      // Create and load the Salesforce bootstrap script
      const salesforceScript = document.createElement('script')
      salesforceScript.src =
        'https://renlearn.my.site.com/ESWRLIProductSupportCh1733220094443/assets/js/bootstrap.min.js'
      salesforceScript.id = 'salesforce-bootstrap-script'
      salesforceScript.type = 'text/javascript'
      salesforceScript.onload = () => {
        try {
          // Initialize Embedded Messaging after script loads
          window.embeddedservice_bootstrap.settings.language = 'en_US'
          window.addEventListener('onEmbeddedMessagingReady', () => {
            console.log('Embedded Messaging is ready')
          })
          window.embeddedservice_bootstrap.init(
            '00DHr000001sf9h',
            'RLI_Product_Support_Chat',
            'https://renlearn.my.site.com/ESWRLIProductSupportCh1733220094443',
            {
              scrt2URL: 'https://renlearn.my.salesforce-scrt.com'
            }
          )
          resolve()
        } catch (err) {
          console.error('Error initializing Embedded Messaging:', err)
          reject(err)
        }
      }
      salesforceScript.onerror = () => reject('Failed to load Salesforce script')
      document.body.appendChild(salesforceScript)
    } else {
      resolve() // Script already loaded
    }
  })
}

const openSupportChat = async (): Promise<void> => {
  try {
    await loadForethoughtScripts()
    await loadSalesforceScripts()
    if (window.Forethought !== 'undefined') {
      window.Forethought('widget', 'show')
      window.Forethought('widget', 'open')
    } else {
      console.error('Forethought widget is not initialized')
    }
  } catch (error) {
    console.error(error)
  }
}

function Chat({onCloseModal}: {onCloseModal: () => void}) {
  const {t} = useSafeTranslation()

  const onClick = () => {
    openSupportChat()
    onCloseModal()
  }

  return (
    <Button style="tertiary" size="md" className={panel} onClick={onClick}>
      <div className={panelContent}>
        <FontAwesomeIcon icon={faComments} size="4x" />
        <div className={panelText}>
          <p>{t('SUPPORT_BUTTON_ADMIN_CHAT_TEXT')}</p>
        </div>
      </div>
    </Button>
  )
}

function Email({countryCode}: {countryCode: string | null}) {
  const {t} = useSafeTranslation()

  const supportLink = getAdminSupportEmail(countryCode)

  return (
    <Link size="md" className={panel} href={supportLink} target="" reactRouter={false}>
      <div className={panelContent}>
        <FontAwesomeIcon icon={faEnvelope} size="4x" />
        <div className={panelText}>
          <p>{t('ADMIN_SUPPORT_TEXT')}</p>
        </div>
      </div>
    </Link>
  )
}
